import type { Organization } from '@st/sdk'
import { Context, Effect, Layer, PubSub, Result, Stream } from 'effect'
import { match } from '@st/pattern'
import { routes } from '~/features/routes'
import { Auth } from './auth'
import { Routing } from './routing'
import { SdkClient, type SdkSend } from './sdk-client'

export type OrganizationContextState =
  | { status: 'idle'; currentUserId: string | null; organizationSlug: null }
  | {
      status: 'loading'
      currentUserId: string | null
      organizationSlug: string
    }
  | {
      status: 'loadFailed'
      currentUserId: string | null
      organizationSlug: string
    }
  | {
      status: 'unavailable'
      currentUserId: string | null
      organizationSlug: string
    }
  | {
      status: 'loaded'
      currentUserId: string | null
      organizationSlug: string
      organization: Organization
    }

type OrganizationLookupState =
  | { status: 'loading' | 'loadFailed' | 'unavailable'; organizationSlug: string }
  | { status: 'loaded'; organizationSlug: string; organization: Organization }

type OrganizationContextService = {
  states: Stream.Stream<OrganizationContextState>
  retry: Effect.Effect<void>
}

export class OrganizationContext extends Context.Service<
  OrganizationContext,
  OrganizationContextService
>()('OrganizationContext') {}

export const observeOrganizationContextStates = Stream.unwrap(
  Effect.map(Effect.service(OrganizationContext), (context) => context.states)
)

export const retryOrganizationContext = Effect.flatMap(
  Effect.service(OrganizationContext),
  (context) => context.retry
)

/**
 * Resolves the active route's organization once for the whole application.
 *
 * The shared stream replays its latest state to every module. A route change
 * replaces the lookup and interrupts its old work. Authentication is combined
 * into the result but never owns or restarts the lookup, so logging in cannot
 * consume a response and flash the same route back to loading. Modules consume
 * these facts instead of each orchestrating route, auth, and SDK state.
 */
export function organizationContextLayer() {
  return Layer.effect(
    OrganizationContext,
    Effect.gen(function* () {
      const routing = yield* Routing
      const auth = yield* Auth
      const sdk = yield* SdkClient
      const retries = yield* PubSub.unbounded<void>()

      const routeOrganizationSlugs = routing.locations.pipe(
        Stream.map((location) => {
          const route = routes.match(location.pathname)
          return route && 'organizationSlug' in route ? route.organizationSlug : null
        }),
        Stream.changes
      )
      const retriedOrganizationSlugs = Stream.fromPubSub(retries).pipe(
        Stream.mapEffect(() => routing.currentLocation),
        Stream.map((location) => {
          const route = routes.match(location.pathname)
          return route && 'organizationSlug' in route ? route.organizationSlug : null
        })
      )

      const currentUserIds = Stream.concat(
        Stream.succeed<string | null>(null),
        auth.statuses.pipe(
          Stream.filterMap((event) =>
            match(event)
              .returnType<Result.Result<string | null, void>>()
              .with({ type: 'loggedIn' }, ({ user }) => Result.succeed(user.id))
              .with({ type: 'loggedOut' }, () => Result.succeed(null))
              .with({ type: 'loginFailed' }, () => Result.failVoid)
              .exhaustive()
          )
        )
      ).pipe(Stream.changes)

      const states = Stream.merge(routeOrganizationSlugs, retriedOrganizationSlugs).pipe(
        Stream.switchMap((organizationSlug): Stream.Stream<OrganizationContextState> => {
          if (!organizationSlug) {
            return currentUserIds.pipe(
              Stream.map(
                (currentUserId) =>
                  ({
                    status: 'idle',
                    currentUserId,
                    organizationSlug: null
                  }) satisfies OrganizationContextState
              )
            )
          }

          return organizationStatesForSlug(organizationSlug, currentUserIds, sdk.send)
        })
      )

      return {
        states: yield* Stream.share(states, {
          capacity: 'unbounded',
          replay: 1
        }),
        retry: PubSub.publish(retries, undefined).pipe(Effect.asVoid)
      }
    })
  )
}

function organizationStatesForSlug(
  organizationSlug: string,
  currentUserIds: Stream.Stream<string | null>,
  send: SdkSend
): Stream.Stream<OrganizationContextState> {
  const lookup = Stream.concat(
    Stream.succeed({ status: 'loading', organizationSlug } satisfies OrganizationLookupState),
    Stream.fromEffect(loadOrganization(send, organizationSlug))
  )

  return Stream.concat(
    Stream.zipLatest(lookup, currentUserIds).pipe(
      Stream.map(([state, currentUserId]) => ({ ...state, currentUserId }))
    ),
    // Retain the terminal lookup fact after either finite input completes.
    // Route replacement or retry interrupts this interval through the outer switch.
    Stream.never
  )
}

function loadOrganization(send: SdkSend, organizationSlug: string) {
  return send({
    type: 'organizations/getOrganizationBySlug',
    organizationSlug
  }).pipe(
    Effect.map(
      (organization) =>
        ({
          status: 'loaded',
          organizationSlug,
          organization
        }) satisfies OrganizationLookupState
    ),
    Effect.catchTags({
      TransportError: () =>
        Effect.succeed({
          status: 'loadFailed',
          organizationSlug
        } satisfies OrganizationLookupState),
      TimeoutError: () =>
        Effect.succeed({
          status: 'loadFailed',
          organizationSlug
        } satisfies OrganizationLookupState),
      HttpStatusError: (error) => {
        if (
          error.response.status == 401 ||
          error.response.status == 403 ||
          error.response.status == 404
        ) {
          return Effect.succeed({
            status: 'unavailable',
            organizationSlug
          } satisfies OrganizationLookupState)
        }
        // Any other status fails the lookup like a network failure; the SDK
        // client reports server errors.
        return Effect.succeed({
          status: 'loadFailed',
          organizationSlug
        } satisfies OrganizationLookupState)
      }
    })
  )
}
